import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Row, Col, Card, Button, Modal, Form, Spinner } from 'react-bootstrap';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import CommonTable from '../components/CommonTable/CommonTable';
import PersonalVaultUnlockAlert from './PersonalVaultUnlockAlert';
import CredentialVaultNavUnlockModal from './CredentialVaultNavUnlockModal';
import CredentialVaultInitInfoPanel from './CredentialVaultInitInfoPanel';
import {
  credentialVaultListNavigateState,
  CredentialVaultGateShell,
  useCredentialVaultCrypto,
} from './VaultCryptoProvider';
import { preparePersonalVaultSetup } from '../../crypto/credentialVaultCrypto';
import { listCredentials, deleteCredential, setupPersonalVaultApi } from '../../services/CredentialVaultService';
import '../../css/SearchFilterPanel.css';
import '../settings/commonstyle.css';
import '../tasks/tasklist.css';
import './credentialVault.css';

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

/**
 * @param {{ scope: 'corporate' | 'personal' }} props
 */
export function CredentialVaultListView({ scope }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(() => searchParams.get('search') || '');
  const debouncedSearchText = useDebounce(searchText, 500);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1, totalItems: 0, itemsPerPage: 20 });
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const prevDebouncedRef = useRef(debouncedSearchText);

  const isPersonal = scope === 'personal';
  const vaultListNavState = () =>
    credentialVaultListNavigateState(isPersonal ? 'personal' : 'corporate', searchText);

  const {
    vaultInitialized,
    personalVaultInitialized,
    personalDek,
    unlockPersonalVaultSession,
    vaultIdleTimeoutMinutes,
    retainPersonalVaultDekAfterSetup,
    skipPersonalListPasswordPromptOnce,
  } = useCredentialVaultCrypto();

  const showPersonalVaultInit = isPersonal && !personalVaultInitialized;

  /** Unlock once on the personal list so detail hover/copy does not prompt again (until idle lock). */
  const needsPersonalUnlockOnList =
    isPersonal &&
    personalVaultInitialized &&
    !personalDek &&
    !showPersonalVaultInit &&
    !skipPersonalListPasswordPromptOnce;

  const [personalSetupPw, setPersonalSetupPw] = useState('');
  const [personalSetupPw2, setPersonalSetupPw2] = useState('');
  const [showPersonalSetupPw, setShowPersonalSetupPw] = useState(false);
  const [showPersonalSetupPw2, setShowPersonalSetupPw2] = useState(false);
  const [personalSetupBusy, setPersonalSetupBusy] = useState(false);
  const [personalSetupError, setPersonalSetupError] = useState('');
  const [personalPw, setPersonalPw] = useState('');
  const [showPersonalPw, setShowPersonalPw] = useState(false);
  const [personalUnlockBusy, setPersonalUnlockBusy] = useState(false);
  const [personalUnlockError, setPersonalUnlockError] = useState('');
  const [deleteUnlockOpen, setDeleteUnlockOpen] = useState(false);
  const [deleteUnlockSide, setDeleteUnlockSide] = useState('corporate');
  const deleteCandidateRef = useRef(null);

  useEffect(() => {
    if (!needsPersonalUnlockOnList) return;
    setPersonalPw('');
    setPersonalUnlockError('');
    setShowPersonalPw(false);
  }, [needsPersonalUnlockOnList]);

  const handlePersonalVaultSetup = async (e) => {
    e.preventDefault();
    setPersonalSetupError('');
    setPersonalSetupBusy(true);
    try {
      const bundle = await preparePersonalVaultSetup(personalSetupPw, personalSetupPw2);
      await setupPersonalVaultApi({
        kdfSalt: bundle.kdfSalt,
        authHash: bundle.authHash,
        encryptedDek: bundle.encryptedDek,
      });
      retainPersonalVaultDekAfterSetup(bundle.dek);
      toast.success('Credentials Vault initialized.', {
        position: 'top-right',
        autoClose: 2800,
      });
      setPersonalSetupPw('');
      setPersonalSetupPw2('');
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || 'Could not create personal vault';
      setPersonalSetupError(msg);
    } finally {
      setPersonalSetupBusy(false);
    }
  };

  const handlePersonalListUnlock = async (e) => {
    e.preventDefault();
    setPersonalUnlockError('');
    if (!personalPw.trim()) {
      setPersonalUnlockError('Personal vault password is required');
      return;
    }
    setPersonalUnlockBusy(true);
    try {
      await unlockPersonalVaultSession(personalPw.trim());
      setPersonalPw('');
      setShowPersonalPw(false);
      toast.success('Personal vault unlocked', { position: 'top-right', autoClose: 2500 });
    } catch (err) {
      setPersonalUnlockError(
        err?.response?.data?.message || err?.message || 'Incorrect personal vault password'
      );
    } finally {
      setPersonalUnlockBusy(false);
    }
  };

  const openNavUnlockForAdd = () => {
    if (isPersonal && !personalVaultInitialized) {
      toast.error('Create your personal vault before adding credentials.');
      return;
    }
    if (!isPersonal && !vaultInitialized) {
      toast.error('Corporate Credentials Vault is not available.');
      return;
    }
    navigate('/credential-vault/addCredential', { state: vaultListNavState() });
  };

  const openNavUnlockForEdit = (row) => {
    if (!row?.can_edit_credential) return;
    if (isPersonal && !personalVaultInitialized) {
      toast.error('Create your personal vault before editing credentials.');
      return;
    }
    if (!isPersonal && !vaultInitialized) {
      toast.error('Corporate Credentials Vault is not available.');
      return;
    }
    navigate(`/credential-vault/editCredential/${row.id}`, { state: vaultListNavState() });
  };

  const openDeletePasswordGate = (row) => {
    if (!row?.can_delete_credential) return;
    if (isPersonal && !personalVaultInitialized) {
      toast.error('Create your personal vault before deleting credentials.');
      return;
    }
    if (!isPersonal && !vaultInitialized) {
      toast.error('Corporate Credentials Vault is not available.');
      return;
    }
    const unlockSide = isPersonal ? 'personal' : row.private_uses_personal_dek ? 'personal' : 'corporate';
    setDeleteUnlockSide(unlockSide);
    deleteCandidateRef.current = row;
    setDeleteUnlockOpen(true);
  };

  const handleDeleteUnlockContinue = () => {
    const row = deleteCandidateRef.current;
    deleteCandidateRef.current = null;
    setDeleteUnlockOpen(false);
    if (row) setDeleteItem(row);
  };

  const handleDeleteUnlockHide = () => {
    deleteCandidateRef.current = null;
    setDeleteUnlockOpen(false);
  };

  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right' });

  /** First-time setup on this route — mirror corporate admin init UX. */

  const load = useCallback(
    async (pageNum, searchValue) => {
      try {
        setLoading(true);
        const trimmed = searchValue != null ? String(searchValue).trim() : '';
        const res = await listCredentials({
          page: pageNum,
          limit: 20,
          search: trimmed || undefined,
          scope,
        });
        if (res.data?.success) {
          setRows(res.data.data || []);
          setPagination(res.data.pagination || { totalPages: 1, totalItems: 0, itemsPerPage: 20 });
        } else {
          setRows([]);
        }
      } catch (e) {
        console.error(e);
        notifyError(e?.response?.data?.message || 'Failed to load Credentials');
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [scope]
  );

  useEffect(() => {
    const querySearch = searchParams.get('search') || '';
    if (querySearch !== searchText) {
      setSearchText(querySearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isPersonal && !personalVaultInitialized) {
      setLoading(false);
      setRows([]);
      setPagination({ totalPages: 1, totalItems: 0, itemsPerPage: 20 });
      return;
    }
    const searchChanged = prevDebouncedRef.current !== debouncedSearchText;
    if (searchChanged) {
      prevDebouncedRef.current = debouncedSearchText;
      if (page !== 1) {
        setPage(1);
        return;
      }
      load(1, debouncedSearchText);
      return;
    }
    prevDebouncedRef.current = debouncedSearchText;
    load(page, debouncedSearchText);
  }, [page, debouncedSearchText, load, isPersonal, personalVaultInitialized]);

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setDeleteLoading(true);
    try {
      const res = await deleteCredential(deleteItem.id);
      if (res.data?.success) {
        toast.success('Deleted');
        setDeleteItem(null);
        load(page, debouncedSearchText);
      } else {
        notifyError('Delete failed');
      }
    } catch (e) {
      notifyError(e?.response?.data?.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'title',
        header: 'Title',
        accessor: (row) => row.title ?? '',
      },
      {
        key: 'website',
        header: 'Website',
        headerClassName: 'credential-vault-list-website-col',
        cellClassName: 'credential-vault-list-website-cell',
        accessor: (row) => {
          const website = row.website != null ? String(row.website).trim() : '';
          if (!website) return '—';
          return (
            <span className="credential-vault-list-website-text" title={website}>
              {website}
            </span>
          );
        },
      },
      {
        key: 'provider',
        header: 'Provider',
        accessor: (row) => row.provider_name || '—',
      },
      {
        key: 'user_name',
        header: 'User name',
        accessor: (row) => (row.user_name != null && String(row.user_name).trim() !== '' ? row.user_name : '—'),
      },
    ],
    []
  );

  const getCredentialActions = (row) => {
    const actions = [
      {
        label: 'View details',
        icon: 'fas fa-eye',
        onClick: (r) =>
          navigate(`/credential-vault/credentialDetail/${r.id}`, { state: vaultListNavState() }),
      },
    ];
    if (row.can_edit_credential) {
      actions.push({
        label: 'Edit',
        icon: 'fas fa-edit',
        onClick: (r) => openNavUnlockForEdit(r),
      });
    }
    if (row.can_delete_credential) {
      actions.push({
        label: 'Delete',
        icon: 'fas fa-trash',
        className: 'text-danger',
        onClick: (r) => openDeletePasswordGate(r),
      });
    }
    return actions;
  };

  const emptyMessage = searchText.trim()
    ? 'No credentials match your search.'
    : isPersonal
      ? 'No personal credentials yet.'
      : 'No corporate credentials yet.';

  return (
    <>
      <ToastContainer />
      {showPersonalVaultInit ? (
        <div className="credential-vault-form-page credential-vault-init-page">
          <Row className="g-4 align-items-stretch credential-vault-init-two-col-row">
            <Col xs={12} lg={7} className="credential-vault-init-main-col">
              <div className="credential-vault-settings-layout credential-vault-settings-layout--split">
                <Card className="user-list-card credential-form-card">
                  <Card.Body style={{ overflow: 'visible', padding: '1.5rem' }}>
                    <h1 className="credential-vault-init-title">Credentials Vault Initial Settings</h1>
                    <p className="credential-form-intro credential-vault-init-lead">
                      To protect your Credentials Vault using highly secure method, we require you to create a Master
                      Key that is only available to you and your team.
                    </p>
                    <div className="credential-vault-init-important" role="note">
                      <i className="fas fa-exclamation-triangle credential-vault-init-important__icon" aria-hidden />
                      <div>
                        <strong className="credential-vault-init-important__label">Important: </strong>
                        <span>
                          This password will not be stored and hence cannot be retrieved if lost. If this password is
                          lost, you will not be able to retrieve any of your stored Credentials from flowdo.
                        </span>
                      </div>
                    </div>
                    <Form onSubmit={handlePersonalVaultSetup} autoComplete="off">
                      {personalSetupError ? (
                        <div className="text-danger small mb-3">{personalSetupError}</div>
                      ) : null}
                      <Form.Group className="mb-3">
                        <Form.Label>Master Key (Personal)</Form.Label>
                        <div className="position-relative password-input-wrapper">
                          <Form.Control
                            autoFocus
                            type={showPersonalSetupPw ? 'text' : 'password'}
                            autoComplete="new-password"
                            name="credentialVaultPersonalMasterSetup"
                            value={personalSetupPw}
                            onChange={(ev) => setPersonalSetupPw(ev.target.value)}
                            disabled={personalSetupBusy}
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowPersonalSetupPw((v) => !v)}
                            disabled={personalSetupBusy}
                            aria-label={showPersonalSetupPw ? 'Hide password' : 'Show password'}
                          >
                            <i className={`fas ${showPersonalSetupPw ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                          </button>
                        </div>
                      </Form.Group>
                      <Form.Group className="mb-4">
                        <Form.Label>Confirm Master Key (Personal)</Form.Label>
                        <div className="position-relative password-input-wrapper">
                          <Form.Control
                            type={showPersonalSetupPw2 ? 'text' : 'password'}
                            autoComplete="new-password"
                            name="credentialVaultPersonalMasterSetupConfirm"
                            value={personalSetupPw2}
                            onChange={(ev) => setPersonalSetupPw2(ev.target.value)}
                            disabled={personalSetupBusy}
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowPersonalSetupPw2((v) => !v)}
                            disabled={personalSetupBusy}
                            aria-label={showPersonalSetupPw2 ? 'Hide password' : 'Show password'}
                          >
                            <i className={`fas ${showPersonalSetupPw2 ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                          </button>
                        </div>
                      </Form.Group>
                      <div className="task-form-toolbar toolbar toolbar-bottom pt-2">
                        <button
                          type="submit"
                          className="new-user-btn credential-vault-gate-primary-btn"
                          disabled={personalSetupBusy}
                        >
                          {personalSetupBusy ? (
                            <Spinner animation="border" size="sm" variant="light" className="me-2" />
                          ) : (
                            <i className="fas fa-shield-alt me-2" aria-hidden />
                          )}
                          Initialize Credentials Vault
                        </button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </div>
            </Col>
            <Col xs={12} lg={5} className="credential-vault-init-aside-col">
              <CredentialVaultInitInfoPanel scope="personal" />
            </Col>
          </Row>
          <InternalFooter />
        </div>
      ) : null}

      {!showPersonalVaultInit && !needsPersonalUnlockOnList ? (
        <>
          <Row style={{ marginBottom: '0px !important' }}>
            <Col lg={12}>
              <Card className="user-list-card">
                <Card.Header style={{ display: 'block' }}>
                  <div className="border-0 p-0 pb-2">
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                      {/* <div>
                        <h5 className="mb-1">{listHeading}</h5>
                        <p className="text-muted small mb-0">
                          {isPersonal
                            ? 'Only you can see these entries. They use your personal vault password.'
                            : 'Shared with your organization according to access rules.'}{' '}
                          <Link to={otherListPath} className="credential-vault-list-cross-link">
                            {otherListLabel}
                          </Link>
                        </p>
                      </div> */}
                    </div>
                  </div>
                  <div className="border-0 p-0 pb-3 pt-2">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                      <div className="search-input-group flex-grow-1" style={{ minWidth: 200 }}>
                        <i className="fas fa-search search-icon"></i>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search title, website, provider, user name…"
                          value={searchText}
                          onChange={(e) => {
                            const v = e.target.value;
                            setSearchText(v);
                            setSearchParams((prev) => {
                              const p = new URLSearchParams(prev);
                              if (v) p.set('search', v);
                              else p.delete('search');
                              return p;
                            });
                          }}
                        />
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button type="button" className="new-user-btn" onClick={openNavUnlockForAdd}>
                          <i className="fas fa-plus me-2"></i>
                          Add credential
                        </button>
                      </div>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body style={{ minHeight: '400px', overflow: 'visible', padding: '1.5rem' }}>
                  {loading ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                    </div>
                  ) : (
                    <>
                      <CommonTable
                        columns={columns}
                        data={rows}
                        onRowClick={(row) =>
                          navigate(`/credential-vault/credentialDetail/${row.id}`, {
                            state: vaultListNavState(),
                          })
                        }
                        actions={getCredentialActions}
                        emptyMessage={emptyMessage}
                        emptyStateIcon="fas fa-key"
                        getRowId={(row) => row.id}
                        maxHeight="85vh"
                        minHeight="400px"
                        className="credential-vault-common-table"
                      />
                      {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                          <span className="text-muted small">
                            Page {page} of {pagination.totalPages} ({pagination.totalItems} items)
                          </span>
                          <div>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={page <= 1}
                              onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                              Previous
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              className="ms-1"
                              disabled={page >= pagination.totalPages}
                              onClick={() => setPage((p) => p + 1)}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <InternalFooter />
        </>
      ) : null}

      {!showPersonalVaultInit && needsPersonalUnlockOnList ? (
        <CredentialVaultGateShell>
          <PersonalVaultUnlockAlert
            description={
              <p className="small text-muted mb-3">
                Enter your personal vault password.
                {' '}
                The personal vault locks automatically after {vaultIdleTimeoutMinutes} minute
                {vaultIdleTimeoutMinutes === 1 ? '' : 's'} of inactivity.
              </p>
            }
            password={personalPw}
            onPasswordChange={setPersonalPw}
            showPassword={showPersonalPw}
            onToggleShowPassword={() => setShowPersonalPw((v) => !v)}
            error={personalUnlockError}
            busy={personalUnlockBusy}
            onSubmit={handlePersonalListUnlock}
          />
        </CredentialVaultGateShell>
      ) : null}

      <CredentialVaultNavUnlockModal
        show={deleteUnlockOpen}
        side={deleteUnlockSide}
        purpose="delete"
        onHide={handleDeleteUnlockHide}
        onContinue={handleDeleteUnlockContinue}
      />

      <Modal show={!!deleteItem} onHide={() => setDeleteItem(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Credential</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Remove <strong>{deleteItem?.title}</strong>? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setDeleteItem(null)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={deleteLoading} onClick={confirmDelete}>
            {deleteLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default function CredentialVaultList() {
  return <CredentialVaultListView scope="corporate" />;
}
